import React, { useState, useEffect, useCallback, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';

import CssBaseline from '@material-ui/core/CssBaseline';
import Button from '@material-ui/core/Button';
import AddUserIcon from '@material-ui/icons/PersonAdd';

import { PreLoader } from 'app/components/PreLoader';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import { Helmet } from 'react-helmet-async';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import { useTranslation } from 'react-i18next';
import { messages } from './messages';

import { makeStyles } from '@material-ui/core/styles';

import ConfigParamContext from '../../../../../contexts/config/admin/ConfigParam';

import MaterialTable from '@material-table/core';
import AddBox from '@material-ui/icons/AddBox';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';
// typings are here:
import { Icons } from '@material-table/core';
import { config } from 'config';
import theme from 'styles/simple/theme';

const tableIcons: Icons = {
  Add: forwardRef((props, ref) => (
    <AddBox {...props} ref={ref} color="action" />
  )),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => (
    <DeleteOutline {...props} ref={ref} color="action" />
  )),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => (
    <Edit {...props} ref={ref} color="action" />
  )),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const useStyles = makeStyles(theme => ({
  preloader: {
    margin: '0 auto',
    color: '#007336',
  },
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    color: theme.palette.secondary.light,
    background: theme.palette.primary.main,
  },
  actionButton: {
    marginRight: theme.spacing(2),
    color: theme.palette.secondary.light,
    background: theme.palette.primary.main,
    position: 'absolute',
    right: 0,
  },
  iconButton: {
    marginTop: 16,
    padding: 10,
    background: theme.palette.primary.dark,
    '&:hover': {
      background: theme.palette.primary.light,
    },
  },
}));

const columns = [
  { title: 'Módulo', field: 'module' },
  { title: 'Grupo', field: 'module' },
  { title: 'Parámetro', field: 'paramName' },
  { title: 'Ambiente', field: 'environment' },
  {
    title: 'Valor',
    field: 'validationState',
    render: rowData => {
      if (rowData.type === 'Object') {
        return 'Objeto';
      } else {
        return rowData.value;
      }
    },
  },
];

export function ConfigParamHomePage() {
  const [open, setOpen] = React.useState(false);
  const [alertTitle, setAlertTitle] = React.useState('');
  const [alertMessage, setAlertMessage] = React.useState('');
  const [showPreloader, setShowPreloader] = useState(false);

  const [page, setPage] = useState(0);
  const [configParamTable, setConfigParamTable] = useState([]);
  const [pageSize, setPageSize] = useState(25);
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  const [configParamIdToDelete, setConfigParamIdToDelete] = useState('');
  const { home, deleteParam } = React.useContext(ConfigParamContext);

  const handleClose = () => {
    setOpen(false);
  };

  const getConfigParamTable = useCallback(() => {
    new Promise((resolve, reject) => {
      setIsFetching(true);
      setShowPreloader(true);
      home({ page: page + 1 }).then(resp => {
        setShowPreloader(false);
        setIsFetching(false);
        if (resp.respCode === 0) {
          setConfigParamTable(resp.data.params);
          setTotalRowsCount(resp.data.rowsCount);
          setPageSize(resp.data.pageSize);
        }
      });
    });
  }, [home, page]);

  const handleConfirm = () => {
    deleteParam({ _id: configParamIdToDelete })
      .then(resp => {
        setShowPreloader(false);
        if (resp.respCode === 0) {
          const ut = configParamTable.filter(obj => {
            return (obj as configParamDTO)._id !== configParamIdToDelete;
          });
          setConfigParamTable(ut);
          setOpen(false);
        } else {
          setAlertTitle('Borrar Parámetro');
          setAlertMessage(resp.message);
          setOpen(true);
        }
      })
      .catch(resp => {
        setShowPreloader(false);
        setAlertTitle('Borrar Parámetro');
        setAlertMessage(resp.message);
        setOpen(true);
      });
  };

  const navigate = useNavigate();

  const onEditParam = useCallback(
    rowData => {
      navigate(
        config.react_routes.prefix +
          config.react_routes.modules.config.prefix +
          config.react_routes.modules.config.services.editParam.replace(
            ':id',
            rowData._id,
          ),
      );
    },
    [navigate],
  );

  type configParamDTO = {
    _id: string;
    paramName: string;
    type: string;
    environment: string;
    module: string;
    group: string;
    subgroup: string;
    hierarchy: string;
    value: string;
  };

  const onDeleteParam = rowData => {
    setConfigParamIdToDelete(rowData._id);
    setShowPreloader(false);
    setAlertTitle('Borrar Parámetro');
    setAlertMessage(
      'Para confirmar la eliminación del parámetro presione el botón continuar',
    );
    setOpen(true);
  };

  useEffect(() => {
    setShowPreloader(true);
    getConfigParamTable();
  }, [setShowPreloader, home, page, getConfigParamTable]);

  const { t } = useTranslation();

  const classes = useStyles();

  return (
    <Container component="main" maxWidth="md">
      <Helmet>
        <title>{t(messages.i18nPageTitle())}</title>
        <meta name="description" content="{t(messages.i18nDescription())}" />
      </Helmet>

      <Container component="main" maxWidth="xl">
        <CssBaseline />
        <div className={classes.paper}>
          <Typography component="h1" variant="h5">
            Parámetros del Sistema
          </Typography>

          {showPreloader ? <PreLoader /> : null}

          <MaterialTable
            icons={tableIcons}
            columns={columns}
            isLoading={isFetching}
            page={page}
            totalCount={totalRowsCount}
            data={configParamTable}
            onPageChange={p => {
              setPage(p);
              getConfigParamTable();
            }}
            options={{
              pageSize,
              loadingType: 'linear',
              search: false,
              paging: true,
              sorting: false,
              filtering: false,
              showTitle: false,
              columnResizable: false,
              exportAllData: false,
              padding: 'dense',
              pageSizeOptions: [pageSize, 100, 200],
              paginationType: 'normal',
              actionsColumnIndex: -1,
              headerStyle: {
                backgroundColor: theme.palette.primary.light,
                color: '#FFF',
                whiteSpace: 'nowrap',
                textAlign: 'center',
              },
              rowStyle: {
                backgroundColor: '#eee',
                whiteSpace: 'nowrap',
              },
            }}
            localization={{
              pagination: {
                labelDisplayedRows: '{from}-{to} de {count}',
                labelRowsSelect: 'filas',
                labelRowsPerPage: 'Filas por página',
                firstAriaLabel: 'Primera Página',
                firstTooltip: 'Primera Página',
                previousAriaLabel: 'Página Anterior',
                previousTooltip: 'Página Anterior',
                nextAriaLabel: 'Página Siguiente',
                nextTooltip: 'Página Siguiente',
                lastAriaLabel: 'Última Página',
                lastTooltip: 'Última Página',
              },
              toolbar: {
                nRowsSelected: '{0} usuario(s) seleccionado(s)',
                addRemoveColumns: 'Agregar o quitar columnas',
                showColumnsTitle: 'Mostrar Columnas',
                showColumnsAriaLabel: 'Mostrar Columnas',
                exportTitle: 'Exportar',
                exportAriaLabel: 'Exportar',
                searchTooltip: 'Buscar',
                searchPlaceholder: 'Busar',
              },
              header: {
                actions: 'Acciones',
              },
              body: {
                emptyDataSourceMessage: 'Sin parámetros para mostrar',
                addTooltip: 'Agregar',
                deleteTooltip: ' Borrar',
                editTooltip: 'Editar',
                filterRow: {
                  filterTooltip: 'Filtro',
                  filterPlaceHolder: '',
                },
                editRow: {
                  deleteText: 'Confirma el borrado?',
                  cancelTooltip: 'Cancelar',
                  saveTooltip: 'Guardar',
                },
              },
              grouping: {
                placeholder: 'Arrastrar encabezado...',
                groupedBy: 'Agrupado por:',
              },
            }}
            actions={[
              {
                icon: () => <Edit />,
                tooltip: 'Editar Parámetro',
                onClick: (event, rowData) => onEditParam(rowData),
              },
              rowData => ({
                icon: () => <DeleteOutline />,
                tooltip: 'Borrar Parámetro',
                onClick: (event, rowData) => onDeleteParam(rowData),
              }),
              {
                icon: () => (
                  <div>
                    <AddUserIcon />
                    <span style={{ fontSize: '12pt', padding: '0 0 0 10px' }}>
                      Agregar
                    </span>
                  </div>
                ),
                tooltip: 'Agregar Parámetro',
                isFreeAction: true,
                onClick: () => {
                  navigate(
                    config.react_routes.prefix +
                      config.react_routes.modules.config.prefix +
                      config.react_routes.modules.config.services.addParam,
                  );
                },
              },
            ]}
          />
        </div>

        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{alertTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {alertMessage}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant="contained" autoFocus>
              Cancelar
            </Button>
            <Button onClick={handleConfirm} variant="contained" autoFocus>
              Continuar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Container>
  );
}
